import { useCallback, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useGetIdentityHistoryMutation } from '../data-layer/serviceIdentityApi';

/**
 * Fetches service identity history for a given service ID with cursor-based pagination.
 *
 * @param serviceId - The ID of the service identity to fetch history for.
 * @returns An object containing `handleFetchHistory`, `isHistoryLoading`, and `isHistoryError`.
 */
const useGetServiceIdHistory = (serviceId: number | undefined) => {
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // API hooks
  const [fetchHistory, { isError: isHistoryError }] =
    useGetIdentityHistoryMutation();

  // Refs
  const historyAbortRef = useRef<((reason: string) => void) | null>(null);

  const handleFetchHistory = useCallback(
    async (cursor: string | null = null) => {
      setIsHistoryLoading(true);

      if (historyAbortRef.current) {
        historyAbortRef.current('New Search');
      }

      try {
        if (!serviceId) {
          throw new Error('No service ID provided');
        }

        const controller = fetchHistory({ id: serviceId, cursor });

        historyAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        Logger.log('Failed to get history', error);

        return 'Failed to get service identity history';
      } finally {
        setIsHistoryLoading(false);
      }
    },
    [fetchHistory, serviceId]
  );

  return {
    handleFetchHistory,
    isHistoryError,
    isHistoryLoading,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetServiceIdHistory };
